interface OptimisticState<T = any> {
    isOptimistic: boolean
    optimisticData?: T
    isLoading: boolean
    error: string | null
    backgroundError: string | null
}

interface BackgroundTaskConfig {
    notifyOnError?: boolean
    context?: Record<string, any>
    taskName?: string
}

export class OptimisticManager {
    private static instance: OptimisticManager
    private tasks = new Map<string, OptimisticState>()
    private backgroundTasks = new Set<string>()

    static getInstance(): OptimisticManager {
        if (!OptimisticManager.instance) {
            OptimisticManager.instance = new OptimisticManager()
        }
        return OptimisticManager.instance
    }

    async executeOptimistic<T>(
        taskId: string,
        optimisticData: T,
        backgroundTask: () => Promise<void>,
        config: BackgroundTaskConfig = {}
    ): Promise<T> {
        // Immediately set optimistic state
        this.tasks.set(taskId, {
            isOptimistic: true,
            optimisticData,
            isLoading: false,
            error: null,
            backgroundError: null
        })

        // Execute background task
        this.executeBackground(taskId, backgroundTask, config)

        return optimisticData
    }

    private async executeBackground(
        taskId: string,
        backgroundTask: () => Promise<void>,
        config: BackgroundTaskConfig
    ): Promise<void> {
        this.backgroundTasks.add(taskId)

        try {
            await backgroundTask()
            
            // Success: update state
            const currentState = this.tasks.get(taskId)
            if (currentState) {
                this.tasks.set(taskId, {
                    ...currentState,
                    isOptimistic: false
                })
            }
        } catch (error) {
            console.error(`Background task ${taskId} failed:`, error)
            
            // Update with background error
            const currentState = this.tasks.get(taskId)
            if (currentState) {
                this.tasks.set(taskId, {
                    ...currentState,
                    backgroundError: error instanceof Error ? error.message : String(error)
                })
            }

            // Notify admins if configured
            if (config.notifyOnError) {
                await this.notifyBackgroundError(taskId, error, config)
            }
        } finally {
            this.backgroundTasks.delete(taskId)
        }
    }

    private async notifyBackgroundError(
        taskId: string,
        error: any,
        config: BackgroundTaskConfig
    ): Promise<void> {
        try {
            const errorDetails = {
                message: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined,
                name: error instanceof Error ? error.name : 'BackgroundTaskError',
                url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
                userAgent: typeof window !== 'undefined' ? navigator.userAgent : 'Unknown',
                timestamp: new Date().toISOString(),
                type: 'BACKGROUND_TASK_ERROR',
                additionalContext: {
                    taskId,
                    taskName: config.taskName,
                    ...config.context
                }
            }

            await fetch('/api/error-notification', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ error: errorDetails })
            })
        } catch (notificationError) {
            console.error('Failed to send background error notification:', notificationError)
        }
    }

    getState(taskId: string): OptimisticState | null {
        return this.tasks.get(taskId) || null
    }

    isTaskRunning(taskId: string): boolean {
        return this.backgroundTasks.has(taskId)
    }

    clearTask(taskId: string): void {
        this.tasks.delete(taskId)
        this.backgroundTasks.delete(taskId)
    }

    hasBackgroundError(taskId: string): boolean {
        const state = this.tasks.get(taskId)
        return !!state?.backgroundError
    }

    getBackgroundError(taskId: string): string | null {
        const state = this.tasks.get(taskId)
        return state?.backgroundError || null
    }
}

// Convenience hook for React components
export function useOptimistic<T = any>(taskId: string) {
    const manager = OptimisticManager.getInstance()
    const state = manager.getState(taskId)

    return {
        state,
        isOptimistic: state?.isOptimistic || false,
        isLoading: state?.isLoading || false,
        error: state?.error || null,
        backgroundError: state?.backgroundError || null,
        hasBackgroundError: manager.hasBackgroundError(taskId),
        isTaskRunning: manager.isTaskRunning(taskId),
        optimisticData: state?.optimisticData,
        executeOptimistic: (
            optimisticData: T,
            backgroundTask: () => Promise<void>,
            config?: BackgroundTaskConfig
        ) => manager.executeOptimistic(taskId, optimisticData, backgroundTask, config),
        clearTask: () => manager.clearTask(taskId)
    }
}

export const optimisticManager = OptimisticManager.getInstance()